import {
  IPeriodWiseBarChartValues,
  IPeriodWiseCountryData,
  IPeriodWiseMultiLineDateUsageDataPoints,
  IPeriodWiseSingleLineDateUsageDataPoints,
  IPeriodWiseTrendDataPoints,
  Metric,
  IPeriodWiseCapabilityMultiLineDateUsageDataPoints,
  ITrendDataPoint,
  IBarChartValues,
  IMultiLineDateUsageDataPoints,
  IDateUsageDataPoint
} from "@tdp/modules/apps";

import { IAnalyticsV2APIResponseMetric } from "../abstractions/IAnalyticsAPI";

// Usage by Metrics
export const convertToActiveUserTrendData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseTrendDataPoints>((acc, metric) => {
    const periodTrendData: ITrendDataPoint[] = acc[metric.period] ?? [];
    periodTrendData.push({
      value: metric.latestUsage?.usage ?? 0,
      stringValue: metric.latestUsage?.moMString ?? '',
      percent: metric.latestUsage?.moMPercent ?? 0,
      key: metric.period
    });

    acc[metric.period] = periodTrendData;

    return acc;
  }, {})


export const convertToUsageSingleLineData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseSingleLineDateUsageDataPoints>((acc, metric) => {
    acc[metric.period] = metric.data ?? [];
    return acc;
  }, {})

export const convertToUsageMultiLineData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseMultiLineDateUsageDataPoints>((acc, metric) => {
    acc[metric.period] = acc[metric.period] ?? {};
    const periodMultiLineData = acc[metric.period] ?? {} as IMultiLineDateUsageDataPoints;
    periodMultiLineData[metric.metricTranslationKey ?? metric.metric] = metric.data ?? [] as IDateUsageDataPoint[];
    acc[metric.period] = periodMultiLineData;
    return acc;
  }, {})

// Usage by region
export const convertToAreaCountryData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseCountryData>((acc, metric) => {
    const periodCountryData = acc[metric.period] ?? [];
    if (metric.region) {
      periodCountryData.push({
        name: metric.region,
        z: metric.latestUsage?.usage ?? 0
      })
    }
    acc[metric.period] = periodCountryData;
    return acc;
  }, {})


// Usage by host 
export const convertToHostMultiLineData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseMultiLineDateUsageDataPoints>((acc, metric) => {
    const periodHostMultiLineData = acc[metric.period] ?? {} as IMultiLineDateUsageDataPoints;
    if (metric.hostTranslationKey) {
      periodHostMultiLineData[metric.hostTranslationKey] = metric.data ?? [] as IDateUsageDataPoint[]
    }
    acc[metric.period] = periodHostMultiLineData;
    return acc;
  }, {})

export const convertToHostBarChartData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseBarChartValues>((acc, metric) => {
    const periodHostBarChartData = acc[metric.period] ?? {} as IBarChartValues;
    if (metric.hostTranslationKey) {
      periodHostBarChartData[metric.hostTranslationKey] = metric.latestUsage?.usage ?? 0;
    }
    acc[metric.period] = periodHostBarChartData;
    return acc;
  }, {})

export const convertToHostTrendData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseTrendDataPoints>((acc, metric) => {
    const periodHostTrendData = acc[metric.period] ?? [] as ITrendDataPoint[];
    if (metric.hostTranslationKey) {
      periodHostTrendData.push(
        {
          value: metric.latestUsage?.usage ?? 0,
          stringValue: metric.latestUsage?.moMString ?? '',
          percent: metric.latestUsage?.moMPercent ?? 0,
          key: metric.hostTranslationKey
        }
      )
    }
    acc[metric.period] = periodHostTrendData;
    return acc;
  }, {})

// Usage by OS
export const convertToOSBarChartData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseBarChartValues>((acc, metric) => {
    const periodBarChartData: IBarChartValues = acc[metric.period] ?? {} as IBarChartValues;
    if (metric.osTranslationKey) {
      periodBarChartData[metric.osTranslationKey] = metric.latestUsage?.usage ?? 0;
    }
    acc[metric.period] = periodBarChartData
    return acc;
  }, {})

export const convertToOSTrendData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseTrendDataPoints>((acc, metric) => {
    const periodOSTrendData = acc[metric.period] ?? [];
    if (metric.osTranslationKey) {
      periodOSTrendData.push(
        {
          value: metric.latestUsage?.usage ?? 0,
          stringValue: metric.latestUsage?.moMString ?? '',
          percent: metric.latestUsage?.moMPercent ?? 0,
          key: metric.osTranslationKey
        }
      )
    }
    acc[metric.period] = periodOSTrendData;
    return acc;
  }, {})

// Usage by Platform
export const convertToPlatformBarChartData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseBarChartValues>((acc, metric) => {
    acc[metric.period] = acc[metric.period] ?? {} as IBarChartValues;
    const platformBarChartData = acc[metric.period] ?? {} as IBarChartValues;
    if (metric.platformTranslationKey) {
      platformBarChartData[metric.platformTranslationKey] = metric.latestUsage?.usage ?? 0;
    }
    acc[metric.period] = platformBarChartData;
    return acc;
  }, {})

// Usage by Capability
export const convertToCapabilityMultiLineData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseCapabilityMultiLineDateUsageDataPoints>((acc, { period, capability, hostTranslationKey, data }) => {
    if (capability && hostTranslationKey) {
      acc[capability] = acc[capability] ?? {} as IPeriodWiseMultiLineDateUsageDataPoints
      acc[capability][period] = acc[capability][period] ?? {} as IMultiLineDateUsageDataPoints
      acc[capability][period][hostTranslationKey] = data ?? [] as IDateUsageDataPoint[]
    }
    return acc;
  }, {});

export const convertToCapabilityTrendData = (metrics: IAnalyticsV2APIResponseMetric[]) =>
  metrics.reduce<IPeriodWiseTrendDataPoints>((acc, metric) => {
    const capabilityTrendData = acc[metric.period] ?? [];
    if (metric.capabilityTranslationKey) {
      capabilityTrendData.push(
        {
          value: metric.latestUsage?.usage ?? 0,
          stringValue: metric.latestUsage?.moMString ?? '',
          percent: metric.latestUsage?.moMPercent ?? 0,
          key: metric.capabilityTranslationKey
        }
      )
    }
    acc[metric.period] = capabilityTrendData;
    return acc;
  }, {})

// Usage Intensity
export const extractUsageByIntensity = (metrics: IAnalyticsV2APIResponseMetric[]) => {
  const median = metrics.find(metric => metric.metric === Metric.Median)
  const percentage = metrics.find(metric => metric.metric === Metric.Percentage5Days)
  const ratio = metrics.find(metric => metric.metric === Metric.Ratio)
  return {
    median: median?.data?.[0]?.usage ?? 0,
    percentage: percentage?.data?.[0]?.usage ?? 0,
    mau: ratio?.data?.[0]?.usage ?? 0
  }
};
